import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { decamelize } from 'src/utils/decamelize';

import { fetchServices } from '../service/service';

import { Package, PackageListItem, PackageSearchResult } from './_types';
import {
  BulkPackagesPayload,
  denormalizeBulkPackages,
  denormalizePackage,
  normalizePackage,
  normalizePackageSearchResult,
} from './normalizers';

interface FetchPackageOptions {
  id: Package['id'];
}

export const createPackage = async (
  data: Partial<Package>
): Promise<Package> => {
  const transformedData = denormalizePackage(data);

  const response = await api.post('/api/v4/fbm/packages/', transformedData);

  return normalizePackage(response.data);
};

export const updatePackage = async (
  data: Partial<Package>
): Promise<Package> => {
  const transformedData = denormalizePackage(data);

  const response = await api.put(
    `/api/v4/fbm/packages/${data.id}`,
    transformedData
  );

  return normalizePackage(response.data);
};

interface FetchPackageListOptions {
  page?: number;
  perPage?: number;
  search?: string;
  sortBy?: string;
  sortDir?: string;
  deleted?: boolean;
  filters?: {
    locationId?: string;
    assignedTrainerId?: string;
    unlimited?: boolean | string | number;
    serviceId?: string;
    hasService?: boolean;
    hasCount?: boolean;
    paymentType?: 'one_time_price' | 'subscription';
    tags?: string[];
  };
}

export const fetchPackageList = async ({
  page,
  perPage,
  search,
  sortBy,
  sortDir,
  deleted,
  filters = {},
}: FetchPackageListOptions): Promise<{
  total: number;
  packages: Package[];
}> => {
  const response = await api.get('/api/v4/fbm/packages', {
    params: {
      for_user: true,
      page,
      per: perPage,
      deleted: deleted || undefined,
      'q[name_cont]': search,
      ...(sortBy && {
        'q[s]': `${decamelize(sortBy || '')} ${sortDir || 'desc'}`,
      }),
      'q[location_id_eq]': filters.locationId,
      'q[assigned_trainer_id_eq]': filters.assignedTrainerId,
      'q[has_service]': filters.serviceId,
      'q[has_count]':
        filters.unlimited && String(filters.unlimited) === 'true'
          ? 'unlimited'
          : filters.unlimited && !isNaN(Number(filters.unlimited))
          ? filters.unlimited
          : undefined,
      'q[one_time_payment_present]':
        filters.paymentType === 'one_time_price' ? 1 : undefined,
      'q[processor_plans_not_eq]':
        filters.paymentType === 'subscription' ? '{}' : undefined,
      'q[tags_name_eq_any]':
        filters.tags && filters.tags.length > 0
          ? Array.isArray(filters.tags)
            ? filters.tags
            : JSON.parse(filters.tags)
          : undefined,
    },
  });
  return {
    packages: response.data.package.map(normalizePackage),
    total: response.data.meta.total,
  };
};

export const deletePackage = async (id: Package['id']) => {
  return await api.delete(`/api/v4/fbm/packages/${id}`);
};

export const clonePackage = async (id: Package['id']): Promise<Package> => {
  const response = await api.post(`/api/v4/fbm/packages/${id}/clone/`);

  return normalizePackage(response.data);
};

export const fetchPackage = async ({
  id,
}: FetchPackageOptions): Promise<Package> => {
  const response = await api.get(`/api/v4/fbm/packages/${id}`);
  return normalizePackage(response.data);
};

export interface FetchPackagesOptions {
  ids: Package['id'][];
}

export const packageListQueryKey = ['packageList'];
export const fetchPackageOptions = async (
  trainerId?: Identifier
): Promise<PackageListItem[]> => {
  const response = await api.get('/api/v4/fbm/packages', {
    params: {
      fetch_all: true,
      pluck_columns: 'id,name,contract',
      trainer_id: trainerId,
      'q[s]': 'name asc',
    },
  });

  return response.data;
};

export const fetchPackages = async ({
  ids,
}: FetchPackagesOptions): Promise<Package[]> => {
  const response = await api.get(`/api/v4/fbm/packages/`, {
    params: {
      with_ids: ids,
    },
  });

  let packages = (response.data.package || response.data).map(normalizePackage);

  const services = await fetchServices({ includePrivate: true });

  packages = packages.map((up) => {
    return {
      ...up,
      serviceCounts: up.serviceCounts.map((sc) => {
        return {
          ...sc,
          services: services.filter((s) => sc.serviceIds.includes(s.id)),
        };
      }),
    };
  });

  return packages;
};

export const fetchPackagesByParams = async (
  params = {}
): Promise<Package[]> => {
  const response = await api.get('/api/v4/fbm/packages', {
    params: { ...params },
  });
  const payload = response.data?.package ?? response.data;
  const rows = Array.isArray(payload) ? payload : [];

  return rows.map(normalizePackage);
};

export const packageImage = (pack: Package) => {
  if (pack?.hasImage) {
    return pack.imageUrl;
  } else {
    return undefined;
  }
};

export const searchPackages = async ({
  search,
}: {
  search?: string;
  id?: string | number;
}): Promise<PackageSearchResult[]> => {
  const response = await api.get('/sm/search', {
    params: {
      types: ['packages'],
      limit: 100,
      no_limit: true,
      term: search,
      page: 1,
    },
  });
  return response.data.results['packages'].map((r) =>
    normalizePackageSearchResult(r)
  );
};

// Type alias for external use - can be the same as BulkPackagesPayload
export type BulkAssignPackagesPayload = BulkPackagesPayload;

export const bulkAssignPackages = async (
  payload: BulkAssignPackagesPayload
): Promise<any> => {
  // Use the normalizer to transform payload to API format
  const apiPayload = denormalizeBulkPackages(payload);

  const response = await api.post('/api/v4/fbm/packages/charge/', apiPayload);
  return response.data;
};

export const createPackageThumbnail = async (
  file: File
): Promise<{ id: string; url: string }> => {
  const formData = new FormData();
  formData.append('asset_image[image]', file);
  const response = await api.post('/api/v2/assets/image/package', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};
